/**
 * Security utilities for Aegis application
 * Implements CSRF protection, login anomaly detection, and security helpers
 */

import type { Json } from "@/integrations/supabase/types";
import type { SecurityEventInsert } from "@/lib/database-types";

// ============================================
// CSRF Token Management
// ============================================

const CSRF_TOKEN_KEY = "aegis_csrf_token";
const CSRF_TOKEN_TIMESTAMP_KEY = "aegis_csrf_timestamp";
const CSRF_TOKEN_EXPIRY_MS = 60 * 60 * 1000; // 1 hour

export function generateCsrfToken(): string {
  const token = crypto.randomUUID();
  sessionStorage.setItem(CSRF_TOKEN_KEY, token);
  sessionStorage.setItem(CSRF_TOKEN_TIMESTAMP_KEY, Date.now().toString());
  return token;
}

export function getCsrfToken(): string | null {
  const token = sessionStorage.getItem(CSRF_TOKEN_KEY);
  const timestamp = sessionStorage.getItem(CSRF_TOKEN_TIMESTAMP_KEY);
  
  if (!token || !timestamp) return null;
  
  // Check if token has expired
  if (Date.now() - parseInt(timestamp, 10) > CSRF_TOKEN_EXPIRY_MS) {
    clearCsrfToken();
    return null;
  }
  
  return token;
}

export function validateCsrfToken(token: string): boolean {
  const storedToken = getCsrfToken();
  return storedToken !== null && storedToken === token;
}

export function clearCsrfToken(): void {
  sessionStorage.removeItem(CSRF_TOKEN_KEY);
  sessionStorage.removeItem(CSRF_TOKEN_TIMESTAMP_KEY);
}

export function refreshCsrfToken(): string {
  clearCsrfToken();
  return generateCsrfToken();
}

// ============================================
// Login Anomaly Detection
// ============================================

const LOGIN_ATTEMPTS_KEY = "aegis_login_attempts";
const MAX_FAILED_ATTEMPTS = 5;
const LOCKOUT_DURATION_MS = 15 * 60 * 1000; // 15 minutes
const ATTEMPT_WINDOW_MS = 5 * 60 * 1000; // 5 minutes

export interface LoginAttempt {
  timestamp: number;
  success: boolean;
  email: string;
  ip?: string;
  userAgent?: string;
  location?: string;
}

export interface LoginAnomalyResult {
  isBlocked: boolean;
  remainingAttempts: number;
  lockoutEndsAt?: number;
  isNewDevice: boolean;
  isNewLocation: boolean;
  riskScore: number; // 0-100
}

function getLoginAttempts(): LoginAttempt[] {
  try {
    const data = localStorage.getItem(LOGIN_ATTEMPTS_KEY);
    return data ? JSON.parse(data) : [];
  } catch {
    return [];
  }
}

function saveLoginAttempts(attempts: LoginAttempt[]): void {
  // Keep only last 100 attempts
  const trimmed = attempts.slice(-100);
  localStorage.setItem(LOGIN_ATTEMPTS_KEY, JSON.stringify(trimmed));
}

export function recordLoginAttempt(attempt: LoginAttempt): void {
  const attempts = getLoginAttempts();
  attempts.push(attempt);
  saveLoginAttempts(attempts);
}

export function checkLoginAnomaly(email: string): LoginAnomalyResult {
  const attempts = getLoginAttempts();
  const now = Date.now();
  
  // Get recent failed attempts for this email
  const recentFailedAttempts = attempts.filter(
    (a) => 
      a.email === email && 
      !a.success && 
      now - a.timestamp < ATTEMPT_WINDOW_MS
  );
  
  // Check if currently locked out
  const lastAttempt = attempts.filter((a) => a.email === email).pop();
  const isLockedOut = 
    recentFailedAttempts.length >= MAX_FAILED_ATTEMPTS &&
    lastAttempt &&
    now - lastAttempt.timestamp < LOCKOUT_DURATION_MS;
  
  // Calculate lockout end time
  const lockoutEndsAt = isLockedOut && lastAttempt
    ? lastAttempt.timestamp + LOCKOUT_DURATION_MS
    : undefined;
  
  // Check for new device (simplified - based on user agent)
  const currentUserAgent = navigator.userAgent;
  const previousUserAgents = new Set(
    attempts.filter((a) => a.email === email && a.success).map((a) => a.userAgent)
  );
  const isNewDevice = previousUserAgents.size > 0 && !previousUserAgents.has(currentUserAgent);
  
  // Calculate risk score
  let riskScore = 0;
  if (recentFailedAttempts.length > 0) riskScore += recentFailedAttempts.length * 10;
  if (isNewDevice) riskScore += 20;
  if (isLockedOut) riskScore += 30;
  riskScore = Math.min(100, riskScore);
  
  return {
    isBlocked: isLockedOut,
    remainingAttempts: Math.max(0, MAX_FAILED_ATTEMPTS - recentFailedAttempts.length),
    lockoutEndsAt,
    isNewDevice,
    isNewLocation: false, // Would require geolocation API
    riskScore,
  };
}

export function clearLoginAttempts(email: string): void {
  const attempts = getLoginAttempts();
  const filtered = attempts.filter((a) => a.email !== email);
  saveLoginAttempts(filtered);
}

// ============================================
// Device Fingerprinting (Basic)
// ============================================

import { DeviceFingerprint as DF, generateDeviceFingerprint, generateDeviceId } from './deviceFingerprint';

export type DeviceFingerprint = DF;

export function getDeviceFingerprint(): DeviceFingerprint {
  // Map DeviceFingerprint addition for client-side compatibility
  const fp = generateDeviceFingerprint();
  return fp as DeviceFingerprint;
}

export function hashFingerprint(fp: DeviceFingerprint): string {
  // Keep a deterministic base64-derived hash for storage
  try {
    return btoa(JSON.stringify(fp)).substring(0, 64);
  } catch {
    return generateDeviceId();
  }
}

// ============================================
// Secure Storage Helpers
// ============================================

const KNOWN_DEVICES_KEY = "aegis_known_devices";

export function registerKnownDevice(userId: string): void {
  const fp = getDeviceFingerprint();
  const hash = hashFingerprint(fp);
  const knownDevices = getKnownDevices();
  
  if (!knownDevices[userId]) {
    knownDevices[userId] = [];
  }
  
  if (!knownDevices[userId].includes(hash)) {
    knownDevices[userId].push(hash);
    localStorage.setItem(KNOWN_DEVICES_KEY, JSON.stringify(knownDevices));
  }
}

export function isKnownDevice(userId: string): boolean {
  const fp = getDeviceFingerprint();
  const hash = hashFingerprint(fp);
  const knownDevices = getKnownDevices();
  return knownDevices[userId]?.includes(hash) ?? false;
}

function getKnownDevices(): Record<string, string[]> {
  try {
    const data = localStorage.getItem(KNOWN_DEVICES_KEY);
    return data ? JSON.parse(data) : {};
  } catch {
    return {};
  }
}

// ============================================
// Security Event Logging
// ============================================

export interface SecurityEvent {
  type: "login_success" | "login_failure" | "logout" | "mfa_challenge" | "mfa_success" | "mfa_failure" | "password_reset" | "suspicious_activity" | "monitoring_config_updated" | "structure_registered" | "structure_updated" | "structure_role_updated" | "escalation_acknowledged";
  timestamp: number;
  userId?: string;
  email?: string;
  metadata?: Record<string, unknown>;
}

const SECURITY_EVENTS_KEY = "aegis_security_events";

export function logSecurityEvent(event: Omit<SecurityEvent, "timestamp">): void {
  const events = getSecurityEvents();
  const eventWithTimestamp = { ...event, timestamp: Date.now() };
  events.push(eventWithTimestamp);
  
  // Keep only last 200 events
  const trimmed = events.slice(-200);
  localStorage.setItem(SECURITY_EVENTS_KEY, JSON.stringify(trimmed));
  
  // Log to console in development
  if (import.meta.env.DEV) {
    console.log("[Security Event]", event);
  }
  
  // Also log to Supabase security_events table for server-side audit trail
  // This is fire-and-forget to not block the UI
  logSecurityEventToSupabase(eventWithTimestamp).catch(() => {
    // Silently fail - local storage is the primary log
  });
}

/**
 * Log security event to Supabase for server-side audit trail
 * Only logs when user is authenticated (RLS requires auth)
 */
async function logSecurityEventToSupabase(event: SecurityEvent): Promise<void> {
  try {
    const { supabase } = await import("@/lib/supabase/client");
    
    // Only attempt to log if we have a user ID (authenticated)
    // Unauthenticated events are still logged to localStorage
    if (!event.userId) {
      return;
    }
    
    const insertData: SecurityEventInsert = {
      user_id: event.userId,
      event_type: event.type,
      ip_address: null, // Would need server-side detection
      user_agent: navigator.userAgent,
      metadata: JSON.parse(JSON.stringify(event.metadata ?? {})) as Json,
    };
    
    await supabase.from("security_events").insert(insertData);
  } catch {
    // Silently fail - local storage is the primary log
  }
}

export function getSecurityEvents(): SecurityEvent[] {
  try {
    const data = localStorage.getItem(SECURITY_EVENTS_KEY);
    return data ? JSON.parse(data) : [];
  } catch {
    return [];
  }
}

// ============================================
// Input Sanitization
// ============================================

export function sanitizeInput(input: string): string {
  return input
    .replace(/[<>]/g, "") // Remove potential HTML tags
    .trim();
}

export function isValidEmail(email: string): boolean {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
}

export function isStrongPassword(password: string): { valid: boolean; errors: string[] } {
  const errors: string[] = [];
  
  if (password.length < 12) {
    errors.push("Password must be at least 12 characters long");
  }
  if (!/[A-Z]/.test(password)) {
    errors.push("Password must contain at least one uppercase letter");
  }
  if (!/[a-z]/.test(password)) {
    errors.push("Password must contain at least one lowercase letter");
  }
  if (!/[0-9]/.test(password)) {
    errors.push("Password must contain at least one number");
  }
  if (!/[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(password)) {
    errors.push("Password must contain at least one special character");
  }
  
  return { valid: errors.length === 0, errors };
}

// ============================================
// Session Security
// ============================================

const SESSION_START_KEY = "aegis_session_start";
const MAX_SESSION_DURATION_MS = 24 * 60 * 60 * 1000; // 24 hours

export function startSecureSession(): void {
  sessionStorage.setItem(SESSION_START_KEY, Date.now().toString());
  generateCsrfToken();
}

export function isSessionValid(): boolean {
  const sessionStart = sessionStorage.getItem(SESSION_START_KEY);
  if (!sessionStart) return false;
  
  const elapsed = Date.now() - parseInt(sessionStart, 10);
  return elapsed < MAX_SESSION_DURATION_MS;
}

export function endSecureSession(): void {
  clearCsrfToken();
  sessionStorage.removeItem(SESSION_START_KEY);
}

// ============================================
// Production-Ready Security Features
// ============================================

import { z } from 'zod';

// Rate limiting configuration
export const RATE_LIMIT_CONFIG = {
  windowMs: parseInt(import.meta.env.VITE_RATE_LIMIT_WINDOW_MS || '900000'), // 15 minutes
  max: parseInt(import.meta.env.VITE_RATE_LIMIT_MAX_REQUESTS || '100'), // limit each IP to 100 requests per windowMs
  message: {
    error: 'Too many requests from this IP, please try again later.',
    retryAfter: 'Retry after 15 minutes'
  },
  standardHeaders: true,
  legacyHeaders: false,
};

// Security headers for production
export const SECURITY_HEADERS = {
  'Content-Security-Policy': import.meta.env.VITE_CSP_ENABLED === 'true'
    ? "default-src 'self'; script-src 'self' 'unsafe-inline' 'unsafe-eval' https://js.sentry-cdn.com https://*.supabase.co; style-src 'self' 'unsafe-inline' https://fonts.googleapis.com; font-src 'self' https://fonts.gstatic.com; img-src 'self' data: https: blob:; connect-src 'self' https://*.supabase.co https://sentry.io https://*.sentry.io wss://*.supabase.co; frame-ancestors 'none';"
    : undefined,
  'X-Frame-Options': 'DENY',
  'X-Content-Type-Options': 'nosniff',
  'X-XSS-Protection': '1; mode=block',
  'Referrer-Policy': 'strict-origin-when-cross-origin',
  'Permissions-Policy': 'camera=(), microphone=(), geolocation=()',
  'Strict-Transport-Security': import.meta.env.VITE_ENABLE_HTTPS_ONLY === 'true'
    ? 'max-age=31536000; includeSubDomains; preload'
    : undefined,
};

// Input validation schemas using Zod
export const SECURITY_SCHEMAS = {
  email: z.string()
    .min(1, 'Email is required')
    .max(254, 'Email too long')
    .regex(/^[^\s@]+@[^\s@]+\.[^\s@]+$/, 'Invalid email format'),

  password: z.string()
    .min(12, 'Password must be at least 12 characters')
    .max(128, 'Password too long')
    .regex(/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]/,
           'Password must contain uppercase, lowercase, number, and special character'),

  fullName: z.string()
    .min(2, 'Name must be at least 2 characters')
    .max(100, 'Name too long')
    .regex(/^[a-zA-Z\s'-]+$/, 'Name contains invalid characters'),

  phone: z.string()
    .optional()
    .refine((val) => !val || /^\+?[\d\s\-()]{10,15}$/.test(val), 'Invalid phone format'),

  organizationName: z.string()
    .optional()
    .refine((val) => !val || (val.length >= 2 && val.length <= 100), 'Organization name must be 2-100 characters'),
};

// Rate limiting store (in-memory for client-side)
class RateLimitStore {
  private requests: Map<string, { count: number; resetTime: number }> = new Map();

  check(identifier: string): { allowed: boolean; remaining: number; resetTime: number } {
    const now = Date.now();
    const windowMs = RATE_LIMIT_CONFIG.windowMs;
    const maxRequests = RATE_LIMIT_CONFIG.max;

    const existing = this.requests.get(identifier);

    if (!existing || now > existing.resetTime) {
      // First request or window expired
      this.requests.set(identifier, {
        count: 1,
        resetTime: now + windowMs
      });
      return { allowed: true, remaining: maxRequests - 1, resetTime: now + windowMs };
    }

    if (existing.count >= maxRequests) {
      return { allowed: false, remaining: 0, resetTime: existing.resetTime };
    }

    existing.count++;
    return {
      allowed: true,
      remaining: maxRequests - existing.count,
      resetTime: existing.resetTime
    };
  }

  cleanup() {
    const now = Date.now();
    for (const [key, value] of this.requests.entries()) {
      if (now > value.resetTime) {
        this.requests.delete(key);
      }
    }
  }
}

export const rateLimitStore = new RateLimitStore();

// Clean up expired entries every 5 minutes
if (typeof window !== 'undefined') {
  setInterval(() => rateLimitStore.cleanup(), 5 * 60 * 1000);
}

// Validate environment variables
export const validateEnvironment = () => {
  const requiredVars = [
    'VITE_SUPABASE_URL',
    'VITE_SUPABASE_ANON_KEY',
  ];

  const missing = requiredVars.filter(varName => !import.meta.env[varName]);

  if (missing.length > 0) {
    throw new Error(`Missing required environment variables: ${missing.join(', ')}`);
  }

  // Warn about missing production variables
  const recommendedVars = [
    'VITE_SENTRY_DSN',
    'RESEND_API_KEY',
    'SUPABASE_SERVICE_ROLE_KEY',
  ];

  const missingRecommended = recommendedVars.filter(varName => !import.meta.env[varName]);

  if (missingRecommended.length > 0 && import.meta.env.VITE_APP_ENV === 'production') {
    console.warn(`Missing recommended environment variables: ${missingRecommended.join(', ')}`);
  }
};

// Initialize security on app start
export const initSecurity = () => {
  validateEnvironment();

  // Add security headers to document (client-side)
  if (typeof document !== 'undefined') {
    const metaCSP = document.querySelector('meta[http-equiv="Content-Security-Policy"]');
    if (!metaCSP && SECURITY_HEADERS['Content-Security-Policy']) {
      const meta = document.createElement('meta');
      meta.httpEquiv = 'Content-Security-Policy';
      meta.content = SECURITY_HEADERS['Content-Security-Policy'];
      document.head.appendChild(meta);
    }
  }
};
